/**
 * How the app is being displayed, and on what.
 *
 * One copy, because two features ask the same question for opposite reasons:
 * `InstallPrompt` offers the install, and `web-push` must explain that on iOS
 * background notifications only exist AFTER that install. Two copies of this
 * logic would let the banner and the push panel disagree about the same device.
 */

/** Running as an installed app (Home Screen on iOS, standalone window elsewhere). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in window.navigator &&
      (window.navigator as unknown as { standalone: boolean }).standalone === true)
  )
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window)
}

export function isIOSSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return isIOS() && /Safari/i.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua)
}

/**
 * True when this device CAN do Web Push but only once installed.
 *
 * iOS and iPadOS expose neither `Notification` nor `PushManager` in a plain
 * Safari tab: since 16.4 the Push API is reachable only from a Home Screen
 * web app (https://webkit.org/blog/13878/). So the plain capability check
 * that works everywhere else reports «not supported» on a device where push
 * works perfectly well two taps later, which is the opposite of the truth.
 */
export function needsInstallForPush(): boolean {
  return isIOS() && !isStandalone()
}
